import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, StarRating } from "@/components/Common";
import { formatINR } from "@/lib/format";
import { Flame, ChevronRight } from "lucide-react";

export default function CrossSell() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/cross-sell").then((r) => {
      setGroups(r.data);
      if (r.data.length) setActive(r.data[0].key);
    });
  }, []);

  const current = groups.find((g) => g.key === active);

  return (
    <div className="animate-fade-up">
      <PageHeader title="Cross-Sell Opportunities" subtitle="Clients to approach, ranked by propensity across your scope" />

      {groups.length === 0 ? (
        <div className="card-surface rounded-xl py-16 text-center text-muted-foreground">No cross-sell opportunities right now.</div>
      ) : (
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Product tabs */}
          <div className="space-y-2" data-testid="crosssell-groups">
            {groups.map((g) => (
              <button
                key={g.key}
                onClick={() => setActive(g.key)}
                data-testid={`crosssell-group-${g.key}`}
                className={`w-full flex items-center justify-between rounded-lg px-4 py-3 border row-hover text-left ${
                  active === g.key ? "border-gold/40 bg-gold/10" : "border-border card-surface hover:border-gold/30"
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <Flame className={`h-4 w-4 ${active === g.key ? "text-gold" : "text-muted-foreground"}`} />
                  <span className="text-sm font-medium">{g.product}</span>
                </div>
                <span className="text-xs num px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{g.count}</span>
              </button>
            ))}
          </div>

          {/* Clients list */}
          <div className="lg:col-span-3 card-surface rounded-xl overflow-hidden" data-testid="crosssell-clients">
            <div className="px-5 py-4 border-b border-border">
              <h3 className="font-display font-semibold">Clients to approach for {current?.product}</h3>
              <p className="text-xs text-muted-foreground mt-0.5">{current?.count} high-propensity clients</p>
            </div>
            {current?.clients.map((c) => (
              <div
                key={c.client_id}
                onClick={() => navigate(`/clients/${c.client_id}`)}
                data-testid={`crosssell-client-${c.client_id}`}
                className="flex items-center gap-4 px-5 py-3.5 cursor-pointer row-hover hover:bg-secondary border-b border-border/40 last:border-0"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{c.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{c.reason}</div>
                </div>
                <div className="hidden sm:block"><StarRating value={c.potential} size={13} /></div>
                <div className="text-right hidden md:block">
                  <div className="text-xs text-muted-foreground">AUM</div>
                  <div className="text-sm num font-medium">{formatINR(c.aum)}</div>
                </div>
                <div className="text-right w-14">
                  <div className="text-sm num font-semibold text-gold">{c.score}%</div>
                </div>
                <div className="text-xs text-muted-foreground hidden lg:block">{c.manager_name}</div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
